import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { useAuthStore } from "@/stores/auth";

/**
 * OpenAPI paths in schema.ts are full ("/api/v1/...").
 * So baseUrl is empty and we let vite proxy / nginx pass through.
 */
const baseClient = createClient<paths>({ baseUrl: "" });

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return false;

  refreshing = (async () => {
    try {
      const r = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!r.ok) return false;
      const data = await r.json();
      useAuthStore.getState().setSession({
        accessToken: data.accessToken,
        accessExpiresAt: data.accessExpiresAt,
        refreshToken: data.refreshToken,
        refreshExpiresAt: data.refreshExpiresAt,
        user: data.user,
      });
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = useAuthStore.getState().accessToken;
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    if (request.url.includes("/auth/")) return response;

    const ok = await tryRefresh();
    if (!ok) {
      useAuthStore.getState().clear();
      window.dispatchEvent(new CustomEvent("auth:expired"));
      return response;
    }
    const token = useAuthStore.getState().accessToken;
    const retry = await fetch(request.url, {
      method: request.method,
      headers: (() => {
        const h = new Headers(request.headers);
        if (token) h.set("Authorization", `Bearer ${token}`);
        return h;
      })(),
      body: request.body,
    });
    return retry;
  },
};

baseClient.use(authMiddleware);

export const api = baseClient;

/** Convenience for raw fetch (used by /auth endpoints that don't go through middleware). */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
}
